// src/pages/Catalog/ServiceMaterialsPanel.jsx
//
// Service material recipe / salon BOM panel.
// =============================================================================
//
// PURPOSE
// -------
// Embedded in ServicePage.jsx. Lets the salon configure which concrete products
// are normally consumed when a service is performed. Editing rules never changes
// stock; actual consumption is posted later during visit check.
//
// BUSINESS MODEL
// --------------
// Each service_consumption_rules row is one planned material line for one service:
// service + product + quantity + unit + optional default stock location.
// The rule is a recipe only. It does not create stock movements and does not
// represent real usage. Real usage is generated later as visit_material_consumptions
// when the operational visit checkbox is marked as performed.
//
// UNIT CONVERSION REMINDER
// ------------------------
// Rule quantities may be practical salon quantities such as ml or g. The backend
// converts them to Product.stock_unit_code using Product.content_quantity and
// Product.content_unit_code. The frontend intentionally does not duplicate that
// authoritative conversion logic.
//
// I18N NOTE
// ---------
// Translation keys are used only for labels, helper text and messages. Enum values
// such as fixed, per_duration, manual and create_exception remain stable backend
// codes and are translated only when displayed.
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import ProductPicker from "../../components/catalog/pickers/ProductPicker";
import { searchStockProducts, fetchStockLocations } from "../../services/stockService";
import {
  CONSUMPTION_MODES,
  createServiceConsumptionRule,
  deleteServiceConsumptionRule,
  listServiceConsumptionRules,
  updateServiceConsumptionRule,
} from "../../services/serviceConsumptionService";

import "./ServiceMaterialsPanel.css";

// Local formatting helpers keep the configuration table readable. Backend
// services still validate units, conversion and business rules.
function formatDecimal(value, digits = 3) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toFixed(digits).replace(/\.?0+$/, "");
}

function productName(product, t) {
  return product?.name_public || product?.name || t("catalog:picker.unnamedProduct");
}

function productMeta(product, t) {
  const parts = [
    product?.sku ? t("catalog:picker.sku", { value: product.sku }) : null,
    product?.barcode ? t("catalog:picker.barcode", { value: product.barcode }) : null,
    product?.content_quantity && product?.content_unit_code
      ? t("catalog:serviceMaterials.content", { quantity: formatDecimal(product.content_quantity), unit: product.content_unit_code })
      : null,
    product?.stock_unit_code ? t("catalog:serviceMaterials.stockUnit", { unit: product.stock_unit_code }) : null,
  ].filter(Boolean);

  return parts.join(" · ") || t("catalog:serviceMaterials.noSkuContentDetails");
}

// Default rule form values follow the most common salon scenario: fixed material
// consumption, active rule, automatic addition during visit check, and exception
// creation instead of blocking employees when stock is missing.
function defaultForm() {
  return {
    product_id: "",
    consumption_mode: CONSUMPTION_MODES.FIXED,
    quantity: "",
    unit_code: "",
    duration_unit_minutes: "30",
    default_location_id: "",
    is_required: true,
    is_active: true,
    allow_manual_change: true,
    auto_add_to_visit: true,
    shortage_policy: "create_exception",
    comment: "",
  };
}

/**
 * ServiceMaterialsPanel.
 *
 * This panel edits planned service material rules only. It does not represent a
 * visit and does not post any inventory movement. Runtime posting is performed by
 * visit check / service consumption backend services.
 */
const ServiceMaterialsPanel = ({ serviceId, disabled = false }) => {
  const { t } = useTranslation(["common", "catalog"]);

  const [rules, setRules] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const canLoad = serviceId !== undefined && serviceId !== null && `${serviceId}`.trim() !== "";

  // The mode help text explains the business meaning of the selected mode so the
  // user understands whether quantity is fixed, duration-based or manual.
  const modeHelp = useMemo(() => {
    switch (form.consumption_mode) {
      case CONSUMPTION_MODES.PER_DURATION:
        return t("catalog:serviceMaterials.modeHelp.per_duration");
      case CONSUMPTION_MODES.MANUAL:
        return t("catalog:serviceMaterials.modeHelp.manual");
      default:
        return t("catalog:serviceMaterials.modeHelp.fixed");
    }
  }, [form.consumption_mode, t]);

  // Load rules and stock locations together. Locations are needed because a rule
  // can define the preferred place from which the material should be consumed.
  async function loadData() {
    if (!canLoad) return;

    setLoading(true);
    setError("");

    try {
      const [rulesRows, locationRows] = await Promise.all([
        listServiceConsumptionRules(serviceId),
        fetchStockLocations(),
      ]);

      setRules(Array.isArray(rulesRows) ? rulesRows : []);
      setLocations(Array.isArray(locationRows) ? locationRows : []);
    } catch (err) {
      setError(err?.message || t("catalog:serviceMaterials.messages.loadFailed"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, [serviceId]);

  function patchForm(patch) {
    setForm((prev) => ({ ...prev, ...patch }));
  }

  // When a product is selected, prefill the unit with the product content unit
  // first. That makes material recipes natural for salon users, e.g. 20 ml of oil.
  function handleProductSelect(product) {
    setSelectedProduct(product);
    setForm((prev) => ({
      ...prev,
      product_id: product?.id || "",
      unit_code: product?.content_unit_code || product?.stock_unit_code || prev.unit_code || "pcs",
    }));
  }

  // Add a new planned material rule. This is catalog configuration only; it does
  // not create visit consumption or stock movement rows.
  async function handleAddRule() {
    if (!serviceId || disabled) return;

    setSaving(true);
    setError("");
    setMessage("");

    try {
      await createServiceConsumptionRule(serviceId, form);
      setForm(defaultForm());
      setSelectedProduct(null);
      setMessage(t("catalog:serviceMaterials.messages.added"));
      await loadData();
    } catch (err) {
      setError(err?.message || t("catalog:serviceMaterials.messages.addFailed"));
    } finally {
      setSaving(false);
    }
  }

  // Deleting a rule affects future visit checks only. Historical visit material
  // consumptions keep their source_rule_id/reference data for audit purposes.
  async function handleDeleteRule(ruleId) {
    if (!window.confirm(t("catalog:serviceMaterials.messages.deleteConfirm"))) return;

    setSaving(true);
    setError("");
    setMessage("");

    try {
      await deleteServiceConsumptionRule(ruleId);
      setMessage(t("catalog:serviceMaterials.messages.deleted"));
      await loadData();
    } catch (err) {
      setError(err?.message || t("catalog:serviceMaterials.messages.deleteFailed"));
    } finally {
      setSaving(false);
    }
  }

  // Lightweight toggle updates keep daily rule maintenance quick while still
  // going through the backend service for validation and persistence.
  async function handleToggleRule(rule, field, value) {
    setSaving(true);
    setError("");
    setMessage("");

    try {
      await updateServiceConsumptionRule(rule.id, { [field]: value });
      await loadData();
    } catch (err) {
      setError(err?.message || t("catalog:serviceMaterials.messages.updateFailed"));
    } finally {
      setSaving(false);
    }
  }

  if (!canLoad) {
    return (
      <section className="svc-materialsPanel svc-materialsPanel--empty">
        <h2>{t("catalog:serviceMaterials.title")}</h2>
        <p>{t("catalog:serviceMaterials.emptyHelp")}</p>
      </section>
    );
  }

  return (
    <section className="svc-materialsPanel" aria-label={t("catalog:serviceMaterials.ariaLabel")}>
      <div className="svc-materialsPanel__header">
        <div><h2>{t("catalog:serviceMaterials.title")}</h2><p>{t("catalog:serviceMaterials.description")}</p></div>
        <button type="button" className="svc-btn svc-btn--soft" onClick={loadData} disabled={loading || saving}>{t("common:actions.refresh")}</button>
      </div>

      {error ? <div className="svc-materialsAlert svc-materialsAlert--error">{error}</div> : null}
      {message ? <div className="svc-materialsAlert svc-materialsAlert--success">{message}</div> : null}

      <div className="svc-materialsForm">
        <ProductPicker
          label={t("catalog:serviceMaterials.materialProductLabel")}
          context="service-materials"
          browseLabel={t("catalog:picker.browseProducts")}
          placeholder={t("catalog:serviceMaterials.materialProductPlaceholder")}
          selectedProduct={selectedProduct}
          onSelect={handleProductSelect}
          searchProducts={searchStockProducts}
          disabled={disabled || saving}
          helperText={t("catalog:serviceMaterials.materialProductHelper")}
        />

        {selectedProduct ? <div className="svc-materialsProductHint"><strong>{productName(selectedProduct, t)}</strong><span>{productMeta(selectedProduct, t)}</span></div> : null}

        <div className="svc-materialsGrid">
          <label><span>{t("catalog:serviceMaterials.fields.consumptionMode")}</span>
            <select value={form.consumption_mode} onChange={(e) => patchForm({ consumption_mode: e.target.value })} disabled={disabled || saving}>
              <option value={CONSUMPTION_MODES.FIXED}>{t("catalog:serviceMaterials.options.fixed")}</option>
              <option value={CONSUMPTION_MODES.PER_DURATION}>{t("catalog:serviceMaterials.options.perDuration")}</option>
              <option value={CONSUMPTION_MODES.MANUAL}>{t("catalog:serviceMaterials.options.manualOptional")}</option>
            </select>
            <small>{modeHelp}</small>
          </label>

          <label><span>{t("catalog:serviceMaterials.fields.quantity")}</span>
            <input type="number" step="0.001" min={form.consumption_mode === CONSUMPTION_MODES.MANUAL ? "0" : "0.001"} value={form.quantity} onChange={(e) => patchForm({ quantity: e.target.value })} disabled={disabled || saving} placeholder={t("catalog:serviceMaterials.placeholders.quantity")} />
          </label>

          <label><span>{t("catalog:serviceMaterials.fields.unitCode")}</span>
            <input value={form.unit_code} onChange={(e) => patchForm({ unit_code: e.target.value })} disabled={disabled || saving} placeholder={t("catalog:serviceMaterials.placeholders.unitCode")} />
            <small>{t("catalog:serviceMaterials.hints.unitConversion")}</small>
          </label>

          {form.consumption_mode === CONSUMPTION_MODES.PER_DURATION ? (
            <label><span>{t("catalog:serviceMaterials.fields.durationUnitMinutes")}</span>
              <input type="number" min="1" step="1" value={form.duration_unit_minutes} onChange={(e) => patchForm({ duration_unit_minutes: e.target.value })} disabled={disabled || saving} />
            </label>
          ) : null}

          <label><span>{t("catalog:serviceMaterials.fields.defaultStockLocation")}</span>
            <select value={form.default_location_id} onChange={(e) => patchForm({ default_location_id: e.target.value })} disabled={disabled || saving}>
              <option value="">{t("catalog:serviceMaterials.options.noDefaultLocation")}</option>
              {locations.map((loc) => <option key={loc.id} value={String(loc.id)}>{loc.name} ({loc.code})</option>)}
            </select>
            <small>{t("catalog:serviceMaterials.hints.missingLocation")}</small>
          </label>

          <label><span>{t("catalog:serviceMaterials.fields.comment")}</span>
            <input value={form.comment} onChange={(e) => patchForm({ comment: e.target.value })} disabled={disabled || saving} placeholder={t("catalog:serviceMaterials.placeholders.comment")} />
          </label>
        </div>

        <div className="svc-materialsChecks">
          <label><input type="checkbox" checked={!!form.is_required} onChange={(e) => patchForm({ is_required: e.target.checked })} disabled={disabled || saving} />{t("catalog:serviceMaterials.checks.required")}</label>
          <label><input type="checkbox" checked={!!form.auto_add_to_visit} onChange={(e) => patchForm({ auto_add_to_visit: e.target.checked })} disabled={disabled || saving} />{t("catalog:serviceMaterials.checks.autoAdd")}</label>
          <label><input type="checkbox" checked={!!form.allow_manual_change} onChange={(e) => patchForm({ allow_manual_change: e.target.checked })} disabled={disabled || saving} />{t("catalog:serviceMaterials.checks.allowManualChange")}</label>
          <label><input type="checkbox" checked={!!form.is_active} onChange={(e) => patchForm({ is_active: e.target.checked })} disabled={disabled || saving} />{t("catalog:serviceMaterials.checks.active")}</label>
        </div>

        <button type="button" className="svc-btn svc-btn--primary" onClick={handleAddRule} disabled={disabled || saving || !form.product_id}>{saving ? t("catalog:serviceMaterials.saving") : t("catalog:serviceMaterials.addRule")}</button>
      </div>

      <div className="svc-materialsTableWrap">
        <table className="svc-materialsTable">
          <thead><tr><th>{t("catalog:serviceMaterials.columns.product")}</th><th>{t("catalog:serviceMaterials.columns.mode")}</th><th>{t("catalog:serviceMaterials.columns.quantity")}</th><th>{t("catalog:serviceMaterials.columns.location")}</th><th>{t("catalog:serviceMaterials.columns.flags")}</th><th>{t("catalog:serviceMaterials.columns.actions")}</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan="6">{t("catalog:serviceMaterials.loadingRules")}</td></tr> : rules.length === 0 ? <tr><td colSpan="6">{t("catalog:serviceMaterials.noRules")}</td></tr> : rules.map((rule) => (
              <tr key={rule.id} className={rule.is_active ? "" : "is-muted"}>
                <td><strong>{productName(rule.product, t)}</strong><small>{productMeta(rule.product, t)}</small></td>
                <td>{t(`catalog:serviceMaterials.options.${rule.consumption_mode === CONSUMPTION_MODES.PER_DURATION ? "perDuration" : rule.consumption_mode === CONSUMPTION_MODES.MANUAL ? "manualOptional" : "fixed"}`, { defaultValue: rule.consumption_mode })}</td>
                <td>{formatDecimal(rule.quantity)} {rule.unit_code}{rule.consumption_mode === CONSUMPTION_MODES.PER_DURATION && rule.duration_unit_minutes ? <small>{t("catalog:serviceMaterials.perMinutes", { minutes: rule.duration_unit_minutes })}</small> : null}</td>
                <td>{rule.defaultLocation ? `${rule.defaultLocation.name} (${rule.defaultLocation.code})` : "—"}</td>
                <td><div className="svc-materialsFlags"><span>{rule.is_required ? t("catalog:serviceMaterials.flags.required") : t("catalog:serviceMaterials.flags.optional")}</span><span>{rule.auto_add_to_visit ? t("catalog:serviceMaterials.flags.auto") : t("catalog:serviceMaterials.flags.manual")}</span><span>{rule.is_active ? t("catalog:serviceMaterials.flags.active") : t("catalog:serviceMaterials.flags.inactive")}</span></div></td>
                <td><button type="button" className="svc-btn svc-btn--small" onClick={() => handleToggleRule(rule, "is_active", !rule.is_active)} disabled={saving}>{rule.is_active ? t("catalog:serviceMaterials.actions.deactivate") : t("catalog:serviceMaterials.actions.activate")}</button><button type="button" className="svc-btn svc-btn--small svc-btn--dangerSoft" onClick={() => handleDeleteRule(rule.id)} disabled={saving}>{t("catalog:serviceMaterials.actions.delete")}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default ServiceMaterialsPanel;


